// Tier-gate badge/tooltip copy, DERIVED from the entitlement the backend
// actually returned — never hardcoded.
//
// WHY THIS EXISTS
// Six builder/gallery surfaces rendered a paywall badge whose tooltip said a
// FIXED tier ("Essential Tier feature" / "Premium Tier feature") while the
// component had already destructured the real `requiredSubscription` +
// `subscription` from `useIfUserEligible(...)` and thrown them away. When the
// backend gates a feature at a different tier than the string assumed (or the
// tier map moves), the badge tells the user to buy the wrong plan — and the
// badge ICON/COLOR disagreed with the Upgrade popup the same click opened.
//
// `useIfUserEligible` (src/hooks/useIfUserEligible.js) surfaces both halves of
// the answer from /subscription/permissions/:
//   requiredSubscription -> required_subscription_tier      (numeric, 0..3)
//   subscription         -> required_subscription_tier_name (display name)
// The numeric tier is authoritative here because the hook COALESCES the name to
// "Premium" when the backend sends null — so a bare name can't be trusted to
// mean "the backend said Premium". The name is the fallback for the case where
// the backend sends a name but no numeric tier (or renames a tier).

import { SUB_TIER, TIER_NAME } from "./portraitMedia";

import EssentialPackageIcon from "@assets/images/packages/essentialPackageIcon.svg";
import PremiumPackageIcon from "@assets/images/packages/premiumPackageIcon.svg";
import ProSuiteIcon from "@assets/images/ProSuite.svg";

// Tooltip surface color per tier. Essential blue / Premium purple are the two
// values already in use at the call sites this replaces, kept byte-identical so
// nothing shifts for the tiers those surfaces gate at today. ProSuite gold and
// Free grey come from the subscription-card tier palette
// (SubscriptionCard.styled.jsx) so the badge matches the plan card it upsells.
export const TIER_TOOLTIP_COLOR = Object.freeze({
  [SUB_TIER.FREE]: "#98A2B3",
  [SUB_TIER.ESSENTIAL]: "#325AE9",
  [SUB_TIER.PREMIUM]: "#9256E7",
  [SUB_TIER.PROSUITE]: "#FF9C00",
});

export const TIER_BADGE_ICON = Object.freeze({
  [SUB_TIER.ESSENTIAL]: EssentialPackageIcon,
  [SUB_TIER.PREMIUM]: PremiumPackageIcon,
  [SUB_TIER.PROSUITE]: ProSuiteIcon,
});

// The tier a paywalled surface should upsell when nothing usable came back.
// Matches the hook's own `subscription` fallback so the badge and the hook can
// never disagree.
const DEFAULT_TIER = SUB_TIER.PREMIUM;

const NAME_TO_TIER = Object.freeze(
  Object.entries(TIER_NAME).reduce((acc, [tier, name]) => {
    acc[name.toLowerCase()] = Number(tier);
    return acc;
  }, {})
);

/**
 * Resolve the numeric tier a gate requires.
 *
 * Tier 0 (Free) is treated as "no usable answer": a surface only renders this
 * badge when the user is NOT eligible, so "you need Free" is a backend
 * inconsistency, not something to show a paying user.
 *
 * @param {number|string|null|undefined} requiredSubscription numeric tier
 * @param {string|null|undefined} subscriptionName display name fallback
 * @returns {number} 0..3
 */
export const resolveRequiredTier = (requiredSubscription, subscriptionName) => {
  const numeric = Number(requiredSubscription);
  if (Number.isInteger(numeric) && numeric >= SUB_TIER.ESSENTIAL && numeric <= SUB_TIER.PROSUITE) {
    return numeric;
  }

  const fromName =
    typeof subscriptionName === "string"
      ? NAME_TO_TIER[subscriptionName.trim().toLowerCase()]
      : undefined;
  if (fromName >= SUB_TIER.ESSENTIAL) return fromName;

  return DEFAULT_TIER;
};

/**
 * Resolve the numeric tier to upsell, or `null` when the backend did not name
 * one.
 *
 * `resolveRequiredTier` above always answers with a tier because a TIER-GATED
 * surface must always be able to say which plan unlocks it. A METERED surface
 * is different: the AI Wheel Generator is available on every tier (tiers differ
 * only in the token allowance — owner ruling 2026-07-30), so when a user runs
 * out of tokens the backend sends `required_subscription_tier: null` to mean
 * "no upgrade would give you more". Falling back to Premium there would print
 * an upsell the entitlement explicitly denied.
 *
 * Use this on metered surfaces; use `resolveRequiredTier` on tier gates.
 *
 * @param {number|string|null|undefined} requiredSubscription numeric tier
 * @param {string|null|undefined} subscriptionName display name fallback
 * @returns {number|null} 1..3, or null when there is nothing to upsell
 */
export const resolveUpgradeTierOrNull = (requiredSubscription, subscriptionName) => {
  const numeric = Number(requiredSubscription);
  if (Number.isInteger(numeric) && numeric >= SUB_TIER.ESSENTIAL && numeric <= SUB_TIER.PROSUITE) {
    return numeric;
  }
  const fromName =
    typeof subscriptionName === "string"
      ? NAME_TO_TIER[subscriptionName.trim().toLowerCase()]
      : undefined;
  if (fromName >= SUB_TIER.ESSENTIAL && fromName <= SUB_TIER.PROSUITE) return fromName;
  return null;
};

/** Display name of the tier to upsell on a metered surface, or `null`. */
export const resolveUpgradeTierNameOrNull = (requiredSubscription, subscriptionName) => {
  const tier = resolveUpgradeTierOrNull(requiredSubscription, subscriptionName);
  return tier === null ? null : TIER_NAME[tier];
};

/** Display name of the tier a gate requires, e.g. "Essential". */
export const resolveTierName = (requiredSubscription, subscriptionName) =>
  TIER_NAME[resolveRequiredTier(requiredSubscription, subscriptionName)];

/** The badge tooltip copy, e.g. "Premium Tier feature". */
export const tierFeatureTooltip = (requiredSubscription, subscriptionName) =>
  `${resolveTierName(requiredSubscription, subscriptionName)} Tier feature`;

/** Tooltip surface color for the required tier. */
export const tierTooltipColor = (requiredSubscription, subscriptionName) =>
  TIER_TOOLTIP_COLOR[resolveRequiredTier(requiredSubscription, subscriptionName)];

/** Badge icon (svg url) for the required tier. */
export const tierBadgeIcon = (requiredSubscription, subscriptionName) =>
  TIER_BADGE_ICON[resolveRequiredTier(requiredSubscription, subscriptionName)] ||
  TIER_BADGE_ICON[DEFAULT_TIER];
